import React, { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BreadcrumbGroup, {
  BreadcrumbGroupProps,
} from "@amzn/awsui-components-react/polaris/breadcrumb-group";

import { TAB_ID_KEY, getValueFromUrlParameter } from "../../util/urlUtils";
import {
  LEFT_NAV_LABELS,
  LeftNavPath,
  resolveLeftNavPath,
  ValidationTabId,
} from "./shellRoutes";

/**
 * Typed wrapper around the `LEFT_NAV_LABELS` lookup used to derive the
 * second breadcrumb's text (R2.3). Wrapping the record access in a
 * function whose parameter is explicitly typed as {@link LeftNavPath}
 * keeps the indexing well-typed even when the surrounding call site
 * loses the parameter's narrow type through a type-erased helper such
 * as `React.useMemo` (whose generics are erased if the `react` type
 * declarations are unavailable at the current type-check step).
 */
function labelForLeftNavPath(path: LeftNavPath): string {
  return LEFT_NAV_LABELS[path];
}

/**
 * Cloudscape `BreadcrumbGroup` rendered at the top of every primary
 * Left Nav view inside the App Shell.
 *
 * The trail is derived at render time from `useLocation().pathname`
 * (which reflects the active primary Left Nav item) and, on the
 * Validation_View, from the URL query parameter identified by the
 * existing `TAB_ID_KEY` constant. No breadcrumb items are rendered for
 * the Left Nav Header or the Wiki Link, satisfying R2.7.
 *
 * Trail shape by active Left Nav path (see R2.2 -- R2.4 and design
 * "Components and Interfaces -- AppBreadcrumbs.tsx"):
 *
 * | Left Nav path      | Rendered breadcrumb trail                                                          |
 * | ------------------ | ---------------------------------------------------------------------------------- |
 * | `/v2/search`       | "Devices ASIN Central" (`/v2`) > "ASIN Search" (`/v2/search`)                      |
 * | `/v2/baseline`     | "Devices ASIN Central" > "ASIN Date Baseline" (`/v2/baseline`)                     |
 * | `/v2/launch`       | "Devices ASIN Central" > "Launch" (`/v2/launch`)                                   |
 * | `/v2/validation`   | "Devices ASIN Central" > "Validation" (`/v2/validation`) > `<tab-label>`           |
 *
 * On `/v2/validation` the third breadcrumb text is derived from
 * `TAB_ID_KEY`:
 *
 * - `dashboard`       -> "Validation"
 * - `step-breakdown`  -> "Validation Step Breakdown"
 * - `program-tickets` -> "Program Tickets"
 * - `catalog-issues`  -> "Catalog Issues"
 *
 * When no `TAB_ID_KEY` value is present (or the value is not one of the
 * four known tab ids), the default "Validation" label is used. In
 * Phase 1 the Validation_View route still mounts the legacy
 * `AsinValidationTable` component and does not expose the three
 * non-dashboard tab identifiers, so the third breadcrumb text is
 * effectively "Validation" throughout Phase 1. Wiring the tab-id
 * derivation up front keeps Phase 2 (where the Validation Dashboard
 * replaces `AsinValidationTable`) from having to touch this file.
 *
 * Click behaviour: clicking any breadcrumb calls
 * `event.preventDefault()` on the Cloudscape `onFollow` event and then
 * `navigate(routerRelativePath)` so the App Shell moves between Left
 * Nav views without a full page reload. The Applied Filter is preserved
 * automatically because the single `generateQuery` Redux slice (see
 * R3.4) is the single source of truth for filter state and is
 * unaffected by client-side navigation.
 */

/**
 * User-visible URL prefix for the V2 Route. All `href` values on the
 * breadcrumb items are browser-visible URLs and therefore include this
 * prefix (R2.2 and R2.3), even though the App Shell's `BrowserRouter`
 * is configured with `basename="/v2"` and therefore expects paths
 * relative to that basename inside `navigate()` calls.
 */
const V2_ROUTE_PREFIX = "/v2";

/**
 * Root breadcrumb text mandated by R2.2. The corresponding `href`
 * points at the V2 Route root so that clicking the root breadcrumb
 * lands on `/v2`, which the App Shell then redirects to `/v2/search`
 * per the default-route rule in R1.9.
 */
const ROOT_BREADCRUMB_TEXT = "Devices ASIN Central";
const ROOT_BREADCRUMB_HREF: string = V2_ROUTE_PREFIX;

/**
 * Router-relative path corresponding to {@link ROOT_BREADCRUMB_HREF}.
 * Used by the `onFollow` handler when the user clicks the root
 * breadcrumb: `navigate("/")` moves the router to the App Shell's
 * root, which the `AppShell.tsx` `<Routes>` block redirects to
 * `/search`.
 */
const ROOT_ROUTER_PATH = "/";

/**
 * Router-relative path for the Validation_View. Kept in one place so
 * the third-breadcrumb `href` and the default-tab lookup below cannot
 * drift.
 */
const VALIDATION_PATH: LeftNavPath = "/validation";

/**
 * Display labels for the four Validation Dashboard tab identifiers.
 * The mapping is exhaustive over {@link ValidationTabId} (a
 * discriminated union declared in `shellRoutes.ts`), so adding a new
 * tab identifier there is a compile-time signal that this table needs
 * a matching entry.
 *
 * The `dashboard` entry deliberately maps to "Validation" rather than
 * "Dashboard": R2.6 requires the third breadcrumb text to read
 * "Validation" while the Dashboard_Tab is active.
 */
const VALIDATION_TAB_LABELS: Readonly<Record<ValidationTabId, string>> = {
  dashboard: "Validation",
  "step-breakdown": "Validation Step Breakdown",
  "program-tickets": "Program Tickets",
  "catalog-issues": "Catalog Issues",
};

/**
 * The tab identifier value the App Shell falls back to when the
 * `TAB_ID_KEY` URL parameter is missing or holds an unknown value.
 * Kept in step with R6 AC 2 ("no tab identifier present -> select
 * the dashboard tab").
 */
const DEFAULT_VALIDATION_TAB_ID: ValidationTabId = "dashboard";

/**
 * Convert a router-facing pathname (which may or may not carry the
 * `/v2` prefix, depending on whether the caller is inside the App
 * Shell's `basename="/v2"` router or a plain `MemoryRouter` in a
 * unit test) into the single {@link LeftNavPath} that identifies the
 * active primary Left Nav item.
 *
 * The empty-string / root case delegates to {@link resolveLeftNavPath},
 * which returns the default Left Nav path (`/search`) so that the very
 * first render of the App Shell -- before the `/` -> `/search` redirect
 * fires -- still shows a well-formed two-item breadcrumb trail rather
 * than an inconsistent "Devices ASIN Central" > "" pair (R2.3 requires
 * the second breadcrumb text to be one of the four Left Nav display
 * labels).
 */
function derivePathFromLocation(pathname: string): LeftNavPath {
  const withoutV2Prefix = pathname.startsWith(V2_ROUTE_PREFIX)
    ? pathname.slice(V2_ROUTE_PREFIX.length) || "/"
    : pathname;
  return resolveLeftNavPath(withoutV2Prefix);
}

/**
 * Convert a {@link LeftNavPath} (router-relative, e.g. `/search`) into
 * its browser-visible V2 Route URL (e.g. `/v2/search`) so the Cloudscape
 * `BreadcrumbGroup` renders a real anchor `href` that matches the URL
 * shown in the browser (R2.3).
 */
function toBrowserHref(path: LeftNavPath): string {
  return `${V2_ROUTE_PREFIX}${path}`;
}

/**
 * Narrow the raw `TAB_ID_KEY` value read from the URL to the
 * {@link ValidationTabId} discriminated union. Unknown or missing
 * values collapse to {@link DEFAULT_VALIDATION_TAB_ID}, matching the
 * "no tab identifier present -> select the dashboard tab" rule (R6.2).
 */
function resolveValidationTabId(raw: string): ValidationTabId {
  switch (raw) {
    case "dashboard":
    case "step-breakdown":
    case "program-tickets":
    case "catalog-issues":
      return raw;
    default:
      return DEFAULT_VALIDATION_TAB_ID;
  }
}

/**
 * Convert a browser-visible V2 Route `href` (e.g. `/v2/search`) back
 * into the router-relative path expected by `useNavigate()`, given that
 * the App Shell mounts its `BrowserRouter` with `basename="/v2"`
 * (see `AppShell.tsx`).
 *
 * `/v2` on its own strips to `"/"` so that clicking the root breadcrumb
 * lands at the App Shell's root route, which then redirects to
 * `/search`.
 *
 * Any `href` that does NOT begin with the `/v2` prefix is returned
 * unchanged. This is defensive: the breadcrumb items rendered by this
 * component always carry the `/v2` prefix, so the branch only executes
 * if a future change introduces a router-relative `href` in the trail.
 */
function toRouterPath(href: string): string {
  if (!href.startsWith(V2_ROUTE_PREFIX)) {
    return href;
  }
  const stripped = href.slice(V2_ROUTE_PREFIX.length);
  return stripped.length === 0 ? ROOT_ROUTER_PATH : stripped;
}

export const AppBreadcrumbs: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activePath = useMemo<LeftNavPath>(
    () => derivePathFromLocation(location.pathname),
    [location.pathname]
  );

  // `getValueFromUrlParameter` reads `window.location.search`
  // directly. Adding `location.search` to the dependency array is
  // therefore what actually keeps the memoised trail in sync with
  // router-driven URL changes (e.g. the eventual Phase 2 tab-switch
  // handler that will use `navigate()` to update `TAB_ID_KEY`). In
  // Phase 1 the tab identifier is effectively fixed to `dashboard`, so
  // this dependency does not add rendering work today, but wiring it up
  // now means Phase 2 does not need to revisit this file.
  const items = useMemo<ReadonlyArray<BreadcrumbGroupProps.Item>>(() => {
    const rootItem: BreadcrumbGroupProps.Item = {
      text: ROOT_BREADCRUMB_TEXT,
      href: ROOT_BREADCRUMB_HREF,
    };

    const secondItem: BreadcrumbGroupProps.Item = {
      text: labelForLeftNavPath(activePath),
      href: toBrowserHref(activePath),
    };

    if (activePath !== VALIDATION_PATH) {
      return [rootItem, secondItem];
    }

    const tabId = resolveValidationTabId(
      getValueFromUrlParameter(TAB_ID_KEY, DEFAULT_VALIDATION_TAB_ID)
    );

    const thirdItem: BreadcrumbGroupProps.Item = {
      text: VALIDATION_TAB_LABELS[tabId],
      // The third breadcrumb points at the Validation_View route so
      // that clicking it (from the same view) is a no-op navigation.
      // A future Phase 2 iteration MAY choose to encode the tab
      // identifier in this href so that opening the link in a new tab
      // preserves the sub-tab; that is out of scope for Task 6.
      href: toBrowserHref(VALIDATION_PATH),
    };

    return [rootItem, secondItem, thirdItem];
  }, [activePath, location.search]);

  const handleFollow = useCallback(
    (
      event: CustomEvent<
        BreadcrumbGroupProps.ClickDetail<BreadcrumbGroupProps.Item>
      >
    ) => {
      // R2.5: navigation between breadcrumb items happens without a
      // full page reload. Suppress the browser's default anchor click
      // and delegate to react-router's `navigate()` so the App Shell
      // stays mounted and the single `generateQuery` slice (R3.4)
      // preserves the Applied Filter across the transition.
      event.preventDefault();
      navigate(toRouterPath(event.detail.href));
    },
    [navigate]
  );

  return (
    <BreadcrumbGroup
      // Copy the readonly item array into a mutable one for the
      // Cloudscape prop, which is typed as `ReadonlyArray` under the
      // hood but is declared as `Array` in some component versions.
      // The freshly-allocated array is safe: the memoised `items`
      // array is still the stable dependency and is not mutated.
      items={[...items]}
      onFollow={handleFollow}
      expandAriaLabel="Show path"
      ariaLabel="Breadcrumbs"
    />
  );
};

export default AppBreadcrumbs;
